'use client';

import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '@/hooks/firebase.client.config';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import SentimentHistoryInfo from '@/functions/src/entities/sentimentHistoryInfo';

interface StreakDaysComponentProps {
  userId: string | undefined;
  className?: string;
}

interface ChartDataPoint {
  day: string;
  score: number;
  date: string;
}

export function StreakDaysComponent({ userId, className = '' }: StreakDaysComponentProps) {
  const [streakCount, setStreakCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Calculate streak count from latest consecutive days with sentiment > 60
  const calculateStreak = (history: SentimentHistoryInfo[]): number => {
    if (history.length === 0) return 0;

    // Sort by date descending (latest first)
    const sorted = [...history].sort((a, b) => b.date.getTime() - a.date.getTime());

    let streak = 0;
    for (const record of sorted) {
      if (record.sentimentScore > 60) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  };

  // Fetch sentiment history and calculate streak
  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      setStreakCount(0);
      return;
    }

    const fetchStreak = async () => {
      try {
        setIsLoading(true);
        const historyRef = collection(db, `users/${userId}/sentimentHistory`);
        const q = query(historyRef, orderBy('date', 'desc'), limit(30));
        const snapshot = await getDocs(q);

        const history: SentimentHistoryInfo[] = snapshot.docs.map(doc =>
          SentimentHistoryInfo.fromFirestore(doc.data())
        );

        const count = calculateStreak(history);
        setStreakCount(count);
      } catch (error) {
        console.error('Error fetching sentiment streak:', error);
        setStreakCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStreak();
  }, [userId]);

  // Fetch full history for chart when dialog opens
  const loadHistoryForChart = async () => {
    if (!userId || historyLoading) return;

    try {
      setHistoryLoading(true);
      const historyRef = collection(db, `users/${userId}/sentimentHistory`);
      const q = query(historyRef, orderBy('date', 'asc'));
      const snapshot = await getDocs(q);

      const history: SentimentHistoryInfo[] = snapshot.docs.map(doc =>
        SentimentHistoryInfo.fromFirestore(doc.data())
      );

      // Transform to chart data
      const data: ChartDataPoint[] = history.map((record, index) => ({
        day: `Day ${index + 1}`,
        score: record.sentimentScore,
        date: record.date.toLocaleDateString()
      }));

      setChartData(data);
    } catch (error) {
      console.error('Error loading sentiment history for chart:', error);
      setChartData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleClick = () => {
    setShowDialog(true);
    loadHistoryForChart();
  };

  if (!userId) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={cn(
          'flex items-center gap-1.5 bg-[#0d1117] px-3 py-1.5 rounded-full text-reading-sm border border-slate-700/30',
          'hover:bg-slate-800/50 hover:border-slate-600/50 transition-all duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        ) : (
          <>
            <span className="text-reading-sm">🔥</span>
            <span className="font-semibold text-[#d4a853]">{streakCount}</span>
            <span className="text-[#8b949e] hidden sm:inline">day streak</span>
          </>
        )}
      </button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl bg-[#0d1117] border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-white">
              <TrendingUp className="w-5 h-5 text-[#d4a853]" />
              Your Sentiment Streak History
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current Streak */}
            <div className="flex items-center justify-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/20">
              <span className="text-4xl">🔥</span>
              <div>
                <div className="text-3xl font-bold text-[#d4a853]">{streakCount} Days</div>
                <div className="text-sm text-slate-400">Current Streak</div>
              </div>
            </div>

            {/* Chart */}
            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-800">
              {historyLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#30363d" />
                    <XAxis
                      dataKey="day"
                      stroke="#8b949e"
                      tick={{ fill: '#8b949e', fontSize: 12 }}
                    />
                    <YAxis
                      domain={[0, 100]}
                      stroke="#8b949e"
                      tick={{ fill: '#8b949e', fontSize: 12 }}
                      label={{ value: 'Sentiment Score', angle: -90, position: 'insideLeft', fill: '#8b949e' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1c2128',
                        border: '1px solid #30363d',
                        borderRadius: '8px',
                        color: '#e6edf3'
                      }}
                      labelStyle={{ color: '#8b949e' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#d4a853"
                      strokeWidth={2}
                      dot={{ fill: '#d4a853', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-slate-400">
                  No sentiment history available yet
                </div>
              )}
            </div>

            {/* Info */}
            <div className="text-xs text-slate-500 text-center">
              Includes days with sentiment above 60 by EOD
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
